import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory
} from "react-router";
import i18next from "i18next";
import { CreateView, IndexView, ShowView, withBreadCrumbs } from "lego-gui-lib";
import {
  CreateBarringMatrixEntry,
  ShowBarringMatrixEntry
} from "./entry/views";
import React from "react";
import { inject } from "mobx-react";
import { observer } from "mobx-react-lite";
import { BarringMatrixSchema, BarringMatrixSchemaForm } from "./schema";
import { BarringMatrixEntrySchemaColumns } from "./entry/schema";
import { NestedView } from "../../common/nestedViewComponent";
import { ParentRoute } from "../../core/model/parent";

export const BarringMatrixes = {
  url: "/barringMatrixes",
  handler: (props: RouteComponentProps<any>) => {
    const baseUrl = props.match.url;
    const title = i18next.t("hns:barringMatrix:domain_plural");
    return withBreadCrumbs(
      { to: baseUrl, children: title },
      <>
        <ParentRoute path={baseUrl + "/:id"} dataSourceName={"barringMatrix"} />
        <Switch>
          <Route exact={true} path={baseUrl} component={Index} />
          <Route
            path={baseUrl + "/:id/entries/create/:entryId"}
            component={CreateBarringMatrixEntry}
          />
          <Route
            path={baseUrl + "/:id/entries/create"}
            component={CreateBarringMatrixEntry}
          />
          <Route
            path={baseUrl + "/:id/entries/:entryId"}
            component={ShowBarringMatrixEntry}
          />
          <Redirect from={baseUrl + "/:id/entries"} to={baseUrl + "/:id"} />
          <Route path={baseUrl + "/create/:id"} component={Clone} />
          <Route path={baseUrl + "/create"} component={Create} />
          <Route path={baseUrl + "/:id"} component={Show} />
        </Switch>
      </>
    );
  }
};

const injectDataSource = (comp: any) =>
  inject("barringMatrix", "barringMatrixEntry", "i18n")(observer(comp));

export const Index = injectDataSource(
  ({ barringMatrix, i18n, ...rest }: any) => {
    const title = i18n.t("hns:barringMatrix:domain", { count: 2 });
    return (
      <IndexView
        dataSource={barringMatrix.listDataSource}
        {...rest}
        title={title}
        toolbarButtons={{ delete: true, export: true }}
        columns={BarringMatrixSchema}
      />
    );
  }
);

export const Create = injectDataSource(
  ({ barringMatrix, i18n, match, ...rest }: any) => {
    const title = i18n.t("common:create", {
      domain: "hns:barringMatrix:domain"
    });
    const history = useHistory();
    return withBreadCrumbs(
      { to: match.url + "/edit", children: title },
      <CreateView
        dataSource={barringMatrix.oneDataSource}
        {...rest}
        title={title}
        fields={BarringMatrixSchemaForm}
        id={match.params}
        goBack={() => history.goBack()}
      />
    );
  }
);

export const Show = injectDataSource((props: any) => {
  const { barringMatrix, barringMatrixEntry, match, i18n, ...rest } = props;

  const title = i18n.t("common:edit", { domain: "hns:barringMatrix:domain" });
  const indexTitle = i18n.t("hns:barringMatrixEntry:domain_plural");
  const history = useHistory();

  const baseUrl = match.url;
  const showView = (
    <ShowView
      dataSource={barringMatrix.oneDataSource}
      title={title}
      fields={BarringMatrixSchemaForm}
      id={match.params}
      preloadData={false}
      goBack={() => history.goBack()}
    />
  );

  const indexView = (
    <IndexView
      dataSource={barringMatrixEntry.listDataSource}
      {...rest}
      toolbarButtons={{ delete: true, export: true }}
      rowButtons={{
        edit: true,
        delete: true,
        copy: false,
        export: true
      }}
      title={indexTitle}
      baseUrl="/entries"
      columns={BarringMatrixEntrySchemaColumns}
    />
  );

  return withBreadCrumbs(
    { to: baseUrl + "/edit", children: title },
    <NestedView showView={showView} indexView={indexView} />
  );
});

export const Clone = injectDataSource(
  ({ barringMatrix, i18n, SAN, match, ...rest }: any) => {
    const history = useHistory();
    const title = i18n.t("common:create", {
      domain: "hns:barringMatrix:domain"
    });

    return withBreadCrumbs(
      { to: match.url + "/edit", children: i18n.t("common:copy") },
      <CreateView
        submit={async () => {
          return barringMatrix.oneDataSource.clone();
        }}
        dataSource={barringMatrix.oneDataSource}
        {...rest}
        title={title}
        fields={BarringMatrixSchema}
        id={match.params}
        goBack={() => history.goBack()}
      />
    );
  }
);
